import { ZONE_COLORS as TOKEN_ZONE_COLORS } from "./designTokens";

export const ZONE_COLORS = TOKEN_ZONE_COLORS;

export const HR_ZONE_RANGES = {
  z1: { label: "Z1 · Recovery", range: "<60% Max HR" },
  z2: { label: "Z2 · Endurance", range: "60–70%" },
  z3: { label: "Z3 · Tempo", range: "70–80%" },
  z4: { label: "Z4 · Threshold", range: "80–90%" },
  z5: { label: "Z5 · VO2 Max", range: "≥90%" },
};

export const POWER_ZONE_RANGES = {
  z1: { label: "Z1 · Active Rec.", range: "<55% FTP" },
  z2: { label: "Z2 · Endurance", range: "55–75%" },
  z3: { label: "Z3 · Tempo", range: "75–90%" },
  z4: { label: "Z4 · Threshold", range: "90–105%" },
  z5: { label: "Z5 · VO2+", range: "≥105%" },
};

export function getHrZone(hr, maxHr = 190) {
  if (hr == null || !maxHr) return null;
  const pct = hr / maxHr;
  if (pct < 0.6) return 1;
  if (pct < 0.7) return 2;
  if (pct < 0.8) return 3;
  if (pct < 0.9) return 4;
  return 5;
}

export function getPowerZone(power, ftp) {
  if (power == null || !ftp) return null;
  const pct = power / ftp;
  if (pct < 0.55) return 1;
  if (pct < 0.75) return 2;
  if (pct < 0.9) return 3;
  if (pct < 1.05) return 4;
  return 5;
}

/** Compute z1..z5 second counts from raw samples using the current FTP. */
export function computePowerZonesFromSamples(samples, ftp) {
  if (!ftp || ftp <= 0 || !samples?.length) return null;
  const zones = { z1: 0, z2: 0, z3: 0, z4: 0, z5: 0 };
  let counted = 0;
  for (const s of samples) {
    const p = s?.power;
    if (p == null || Number.isNaN(p)) continue;
    const z = getPowerZone(p, ftp);
    if (z) {
      zones[`z${z}`] += 1;
      counted += 1;
    }
  }
  return counted > 0 ? zones : null;
}

/** Compute z1..z5 second counts from raw samples using max HR (defaults to 190). */
export function computeHrZonesFromSamples(samples, maxHr = 190) {
  if (!samples?.length) return null;
  const zones = { z1: 0, z2: 0, z3: 0, z4: 0, z5: 0 };
  let counted = 0;
  for (const s of samples) {
    const h = s?.hr;
    if (h == null || Number.isNaN(h)) continue;
    const z = getHrZone(h, maxHr);
    if (z) {
      zones[`z${z}`] += 1;
      counted += 1;
    }
  }
  return counted > 0 ? zones : null;
}

export function zoneColorForValue(value, type, threshold) {
  if (value == null || !threshold) return null;
  const zone = type === "power" ? getPowerZone(value, threshold) : getHrZone(value, threshold);
  return zone ? ZONE_COLORS[`z${zone}`] : null;
}

/** Split a metric into z1–z5 line keys so Recharts can render zone-colored segments. */
export function enrichWithZoneLines(data, dataKey, getZoneFn, prefix = "") {
  return data.map((point, i) => {
    const val = point[dataKey];
    const zone = getZoneFn(val);
    const row = { ...point };
    for (let z = 1; z <= 5; z++) row[`${prefix}z${z}`] = null;
    if (zone != null) row[`${prefix}z${zone}`] = val;
    if (i > 0) {
      const prevZone = getZoneFn(data[i - 1][dataKey]);
      if (prevZone != null && zone != null && prevZone !== zone) {
        row[`${prefix}z${prevZone}`] = val;
      }
    }
    return row;
  });
}

export const ZONE_LINE_KEYS = ["z1", "z2", "z3", "z4", "z5"];
